import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, imgUrl, money } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import SafeImage from "../../components/common/SafeImage";
import { Trash2, Minus, Plus, Tag, X, Heart, ShoppingBag, Share2, Ruler } from "lucide-react";
import CartFitSuggestionBanner from "../../components/store/CartFitSuggestionBanner";
import RecommendationsRail from "../../components/store/RecommendationsRail";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

export default function Cart() {
  const { cart, updateQty, removeItem, coupon, setCoupon, wishlist, toggleWishlist, addToCart, refresh: refreshCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const [busyPid, setBusyPid] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareNote, setShareNote] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null);

  const subtotal = (cart.items || []).reduce(
    (s, i) => s + (i.product?.price || 0) * i.qty,
    0
  );
  const discount = coupon?.discount || 0;
  const shipping = (subtotal - discount) > 1999 || subtotal === 0 ? 0 : 99;
  const total = Math.max(0, subtotal - discount + shipping);

  // Saved-for-later: wishlist products NOT currently in the cart
  const cartIds = new Set((cart.items || []).map((i) => i.product_id));
  const savedProducts = (wishlist?.products || []).filter((p) => !cartIds.has(p.id));

  React.useEffect(() => {
    if (!user || !cart.items?.length || coupon) { setSuggested([]); return; }
    let alive = true;
    api.get("/coupons/suggested")
      .then(({ data }) => { if (alive) setSuggested(data || []); })
      .catch(() => { if (alive) setSuggested([]); });
    return () => { alive = false; };
  }, [user, cart.items?.length, coupon]);

  const applyCode = async (raw) => {
    const codeStr = (raw || "").trim();
    if (!codeStr) return;
    setApplying(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: codeStr });
      setCoupon({ code: data.coupon.code, discount: data.discount, description: data.coupon.description });
      setCode("");
      toast.success(`Coupon "${data.coupon.code}" applied — save ${money(data.discount)}!`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const message = /not found or inactive/i.test(detail) ? "Invalid coupon code" : (detail || "Could not apply coupon");
      toast.error(message);
    } finally {
      setApplying(false);
    }
  };
  const apply = () => applyCode(code);

  const removeCoupon = () => {
    setCoupon(null);
    toast.success("Coupon removed");
  };

  const saveForLater = async (item) => {
    if (busyPid) return;
    setBusyPid(item.product_id);
    try {
      // Ensure item is in wishlist, then remove from cart
      const already = wishlist?.product_ids?.includes(item.product_id);
      if (!already) await toggleWishlist(item.product_id);
      await removeItem(item.product_id, item.size);
      toast.success("Moved to Saved for later");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save for later");
    } finally {
      setBusyPid(null);
    }
  };

  const moveToCart = async (product) => {
    if (busyPid) return;
    setBusyPid(product.id);
    try {
      const firstSize = product.sizes?.[0]?.size || null;
      await addToCart(product.id, firstSize, 1);
      await toggleWishlist(product.id);
      toast.success("Added to bag");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to bag");
    } finally {
      setBusyPid(null);
    }
  };

  const removeSaved = async (pid) => {
    if (busyPid) return;
    setBusyPid(pid);
    try {
      await toggleWishlist(pid);
    } finally {
      setBusyPid(null);
    }
  };

  const createShare = async () => {
    if (!cart.items?.length) { toast.error("Add items to your bag before sharing"); return; }
    setSharing(true);
    try {
      const { data } = await api.post("/cart/share", { note: shareNote.trim() });
      setShareResult(data);
      // Immediately open WhatsApp on the user's device
      window.open(data.whatsapp_url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    } finally {
      setSharing(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareResult?.share_url) return;
    try {
      await navigator.clipboard.writeText(shareResult.share_url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  if (!user) {
    return (
      <div className="px-6 py-24 text-center" data-testid="cart-page-signed-out">
        <div className="overline mb-3">Shopping Bag</div>
        <h1 className="font-display text-4xl mb-4">Sign in to view your bag</h1>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 lg:px-24 py-10" data-testid="cart-page">
      <div className="overline mb-2">Shopping Bag</div>
      <h1 className="font-display text-4xl lg:text-5xl mb-10">Your Bazaar edit</h1>

      {cart.items && cart.items.length > 0 && (
        <CartFitSuggestionBanner onApplied={refreshCart} />
      )}

      {(!cart.items || cart.items.length === 0) && savedProducts.length === 0 ? (
        <div>
          <div className="text-center py-20">
            <p className="text-[hsl(var(--kb-muted))] mb-6">Your bag is empty.</p>
            <Link to="/shop" className="kb-btn-primary inline-block">Start browsing</Link>
          </div>
          {/* Empty cart turns into a discovery surface instead of a dead end. */}
          <RecommendationsRail variant="popular" data-testid="cart-empty-popular-rail" />
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-12">
          <div className="space-y-6">
            {(!cart.items || cart.items.length === 0) ? (
              <div className="border border-dashed border-[hsl(var(--kb-border))] py-10 text-center text-sm text-[hsl(var(--kb-muted))]">
                Your bag is empty. Saved items are below.
              </div>
            ) : (
              cart.items.map((item, i) => (
                <div key={i} className="flex gap-4 border-b border-[hsl(var(--kb-border))] pb-6" data-testid={`cart-item-${item.product_id}`}>
                  <Link to={`/product/${item.product?.slug}`} className="w-24 sm:w-28 aspect-[3/4] overflow-hidden bg-[hsl(var(--kb-surface-alt))] shrink-0">
                    <SafeImage src={imgUrl(item.product?.images?.[0])} alt="" className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.product?.slug}`} className="font-display text-lg sm:text-xl line-clamp-2">{item.product?.title}</Link>
                    {item.size && <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">Size: {item.size}</div>}
                    {item.measurement_mode && item.measurement_mode !== "standard_size" && (
                      <div
                        className="inline-flex items-center gap-1 mt-2 text-[10px] uppercase tracking-widest px-2 py-1 bg-[hsl(var(--kb-surface-alt))] text-[hsl(var(--kb-primary))]"
                        data-testid={`fit-badge-${item.product_id}`}
                      >
                        <Ruler size={10} />
                        {item.measurement_mode === "saved_profile" && item.measurement_snapshot?.profile_name
                          ? `Custom fit · ${item.measurement_snapshot.profile_name}`
                          : item.measurement_mode === "appointment_required"
                          ? "Appointment requested"
                          : "Custom fit"}
                      </div>
                    )}
                    <div className="text-[hsl(var(--kb-primary))] mt-2">{money(item.product?.price)}</div>
                    <div className="flex items-center gap-3 sm:gap-4 mt-4 flex-wrap">
                      <div className="flex items-center border border-[hsl(var(--kb-border))]">
                        <button onClick={() => updateQty(item.product_id, item.size, Math.max(1, item.qty - 1))} className="p-2" data-testid={`qty-down-${item.product_id}`}><Minus size={14} /></button>
                        <span className="px-4 text-sm">{item.qty}</span>
                        <button onClick={() => updateQty(item.product_id, item.size, item.qty + 1)} className="p-2" data-testid={`qty-up-${item.product_id}`}><Plus size={14} /></button>
                      </div>
                      <button
                        onClick={() => saveForLater(item)}
                        disabled={busyPid === item.product_id}
                        className="text-xs text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] flex items-center gap-1 disabled:opacity-50"
                        data-testid={`save-later-${item.product_id}`}
                      >
                        <Heart size={14} /> Save for later
                      </button>
                      <button onClick={() => removeItem(item.product_id, item.size)} className="text-xs text-[hsl(var(--kb-muted))] hover:text-red-700 flex items-center gap-1" data-testid={`remove-${item.product_id}`}>
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}

            {savedProducts.length > 0 && (
              <div className="pt-4" data-testid="saved-for-later">
                <div className="overline mb-4 flex items-center gap-2">
                  <Heart size={12} /> Saved for later · {savedProducts.length}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {savedProducts.map((p) => (
                    <div key={p.id} className="border border-[hsl(var(--kb-border))] p-3" data-testid={`saved-${p.id}`}>
                      <Link to={`/product/${p.slug}`}>
                        <div className="aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] overflow-hidden mb-2">
                          {p.images?.[0] && <SafeImage src={imgUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" />}
                        </div>
                        <div className="text-xs line-clamp-2 mb-1">{p.title}</div>
                        <div className="text-xs font-medium text-[hsl(var(--kb-primary))]">{money(p.price)}</div>
                      </Link>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => moveToCart(p)}
                          disabled={busyPid === p.id}
                          className="kb-btn-outline flex-1 !py-1 !px-2 !text-[10px] flex items-center justify-center gap-1 disabled:opacity-50"
                          data-testid={`move-to-cart-${p.id}`}
                        >
                          <ShoppingBag size={10} /> Move
                        </button>
                        <button
                          onClick={() => removeSaved(p.id)}
                          disabled={busyPid === p.id}
                          className="border border-[hsl(var(--kb-border))] hover:border-red-500 px-2 disabled:opacity-50"
                          aria-label="Remove"
                          data-testid={`saved-remove-${p.id}`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="bg-white border border-[hsl(var(--kb-border))] p-6 h-fit">
            <div className="overline mb-4">Order Summary</div>

            {/* Coupon */}
            <div className="border-b border-[hsl(var(--kb-border))] pb-4 mb-4">
              {coupon ? (
                <div className="flex items-center gap-2 bg-[hsl(var(--kb-surface-alt))] px-3 py-2 text-sm" data-testid="applied-coupon">
                  <Tag size={14} className="text-[hsl(var(--kb-primary))]" />
                  <div className="flex-1">
                    <div className="font-mono">{coupon.code}</div>
                    <div className="text-xs text-[hsl(var(--kb-muted))]">−{money(coupon.discount)}{coupon.description ? ` · ${coupon.description}` : ""}</div>
                  </div>
                  <button onClick={removeCoupon} className="text-[hsl(var(--kb-muted))] hover:text-red-700" aria-label="Remove coupon" data-testid="remove-coupon"><X size={14} /></button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input
                      className="kb-input flex-1 uppercase"
                      placeholder="Coupon code"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === "Enter" && apply()}
                      data-testid="coupon-input"
                    />
                    <button className="kb-btn-outline shrink-0" onClick={apply} disabled={applying || !code.trim()} data-testid="apply-coupon-btn">
                      {applying ? "…" : "Apply"}
                    </button>
                  </div>
                  {suggested.length > 0 && (
                    <div className="mt-3" data-testid="suggested-coupons">
                      <div className="overline text-[10px] mb-2">Available offers</div>
                      <div className="space-y-2">
                        {suggested.map((s) => (
                          <button
                            key={s.code}
                            type="button"
                            onClick={() => applyCode(s.code)}
                            disabled={applying}
                            className="w-full flex items-center gap-2 border border-dashed border-[hsl(var(--kb-primary))]/40 hover:border-[hsl(var(--kb-primary))] px-3 py-2 text-left text-xs transition-colors"
                            data-testid={`suggested-${s.code}`}
                          >
                            <Tag size={12} className="text-[hsl(var(--kb-primary))] shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono font-medium">{s.code}</span>
                                <span className="text-green-700">save {money(s.discount)}</span>
                              </div>
                              {s.description && <div className="text-[hsl(var(--kb-muted))] truncate">{s.description}</div>}
                            </div>
                            <span className="text-[10px] uppercase tracking-wider underline shrink-0">Apply</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-between py-2 text-sm"><span>Subtotal</span><span data-testid="cart-subtotal">{money(subtotal)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between py-2 text-sm text-green-700">
                <span>Discount</span><span data-testid="cart-discount">−{money(discount)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 text-sm"><span>Shipping</span><span>{shipping === 0 ? "Complimentary" : money(shipping)}</span></div>
            <div className="flex justify-between py-4 text-lg border-t border-[hsl(var(--kb-border))] mt-2 font-display"><span>Total</span><span data-testid="cart-total">{money(total)}</span></div>
            <button
              className="kb-btn-primary w-full mt-4"
              onClick={() => navigate("/checkout")}
              disabled={!cart.items?.length}
              data-testid="checkout-btn"
            >
              Proceed to Checkout
            </button>
            <button
              className="w-full mt-3 border border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))] text-sm py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:hover:border-[hsl(var(--kb-border))]"
              onClick={() => { setShareNote(""); setShareResult(null); setShareOpen(true); }}
              disabled={!cart.items?.length}
              data-testid="share-bag-btn"
              type="button"
            >
              <Share2 size={14} /> Share bag on WhatsApp
            </button>
          </aside>
        </div>
      )}

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Share your bag</DialogTitle>
            <DialogDescription>
              We'll open WhatsApp on your device — pick who you want to share with.
              The recipient can open the link to view your bag and add it to their own.
            </DialogDescription>
          </DialogHeader>
          {!shareResult ? (
            <div className="space-y-4">
              <div>
                <label className="overline block mb-2">Add a note (optional)</label>
                <textarea
                  className="kb-input min-h-[90px]"
                  placeholder="e.g. Here's the outfit I picked for Diya's wedding!"
                  maxLength={280}
                  value={shareNote}
                  onChange={(e) => setShareNote(e.target.value)}
                  data-testid="share-note-input"
                />
                <div className="text-xs text-[hsl(var(--kb-muted))] mt-1 text-right">{shareNote.length}/280</div>
              </div>
              <button
                onClick={createShare}
                disabled={sharing}
                className="kb-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="share-confirm-btn"
                type="button"
              >
                <Share2 size={14} />
                {sharing ? "Preparing…" : "Open WhatsApp to share"}
              </button>
              <p className="text-xs text-[hsl(var(--kb-muted))] text-center">
                Link is valid for 14 days. No one can see your personal details.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-[hsl(var(--kb-surface-alt))] p-3 text-xs break-all font-mono" data-testid="share-result-url">
                {shareResult.share_url}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href={shareResult.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="kb-btn-primary flex items-center justify-center gap-2"
                  data-testid="share-whatsapp-again"
                >
                  <Share2 size={14} /> WhatsApp
                </a>
                <button
                  onClick={copyShareLink}
                  className="kb-btn-outline flex items-center justify-center gap-2"
                  data-testid="share-copy-link"
                  type="button"
                >
                  Copy link
                </button>
              </div>
              <button
                onClick={() => setShareOpen(false)}
                className="text-xs text-[hsl(var(--kb-muted))] underline w-full text-center"
                type="button"
              >
                Close
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cart-aware recommendations — only when there's at least one
          item, to keep the empty-cart popular rail above the fold. */}
      {cart.items && cart.items.length > 0 && (
        <RecommendationsRail variant="cart" data-testid="cart-recommendations-rail" />
      )}
    </div>
  );
}
